import { delMany, get, set } from 'idb-keyval';

import { PersisterStoreName } from './persister/types';

export class StoreCrypto {
  #key: CryptoKey | undefined;

  private salt: Uint8Array<ArrayBuffer> | undefined;

  private iv: Uint8Array<ArrayBuffer> | undefined;

  public constructor(
    private readonly userId: string,
    private readonly storeName: PersisterStoreName,
  ) {}

  public static async createForUserStore(userId: string, password: string) {
    const userStoreCrypto = new StoreCrypto(userId, 'tinybase-user');
    await userStoreCrypto.generateKey(password);

    return userStoreCrypto;
  }

  private get saltIdbKey() {
    return `${this.storeName}-s`;
  }

  private get ivIdbKey() {
    return `${this.storeName}-iv`;
  }

  public async deleteStoredParams() {
    await delMany([this.saltIdbKey, this.ivIdbKey]);
  }

  private async getSalt() {
    if (this.salt) {
      return this.salt;
    }
    const storedSalt = await get<Uint8Array<ArrayBuffer>>(this.saltIdbKey);

    if (storedSalt) {
      this.salt = storedSalt;
      return storedSalt;
    }
    const newSalt = crypto.getRandomValues(new Uint8Array(16));
    this.salt = newSalt;
    await set(this.saltIdbKey, newSalt);

    return newSalt;
  }

  private async generateIv() {
    const iv = crypto.getRandomValues(new Uint8Array(12));
    this.iv = iv;
    await set(this.ivIdbKey, iv);

    return iv;
  }

  private async getIv() {
    return this.iv || (await get<Uint8Array<ArrayBuffer>>(this.ivIdbKey));
  }

  public async generateKey(password: string) {
    const passwordKey = await crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(password),
      'PBKDF2',
      false,
      ['deriveKey'],
    );

    const salt = await this.getSalt();

    this.#key = await crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 6e5,
        hash: 'SHA-256',
      },
      passwordKey,
      {
        name: 'AES-GCM',
        length: 256,
      },
      true,
      ['encrypt', 'decrypt'],
    );
  }

  public clearKey() {
    this.#key = undefined;
  }

  private getAlgorithm(iv: Uint8Array<ArrayBuffer>): AesGcmParams {
    const additionalData = new TextEncoder().encode(JSON.stringify([this.userId, this.storeName]));

    return {
      name: 'AES-GCM',
      additionalData,
      iv,
      tagLength: 128,
    };
  }

  public async encrypt(data: string): Promise<ArrayBuffer> {
    if (!this.#key) {
      throw Error('Missing encryption key');
    }
    const iv = await this.generateIv();

    return crypto.subtle.encrypt(this.getAlgorithm(iv), this.#key, new TextEncoder().encode(data));
  }

  public async decrypt(encryptedData: ArrayBuffer): Promise<string> {
    if (!this.#key) {
      throw Error('Missing encryption key');
    }
    const iv = await this.getIv();

    if (!iv) {
      throw Error('Missing IV');
    }

    const decryptedData = await crypto.subtle.decrypt(
      this.getAlgorithm(iv),
      this.#key,
      encryptedData,
    );

    return new TextDecoder().decode(decryptedData);
  }
}
